import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab,
  Link,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../services/api';
import { getFileUrl } from '../services/minio';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function Submissions() {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [error, setError] = useState(null);
  const [viewFile, setViewFile] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/submissions');
      const sortedSubmissions = response.data.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      setSubmissions(sortedSubmissions);
      setFilteredSubmissions(sortedSubmissions);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setError('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  useEffect(() => {
    const searchTermLower = searchTerm.toLowerCase();
    const filtered = submissions.filter(submission => {
      return (
        submission.id?.toLowerCase().includes(searchTermLower) ||
        submission.email?.toLowerCase().includes(searchTermLower) ||
        submission.firstName?.toLowerCase().includes(searchTermLower) ||
        submission.lastName?.toLowerCase().includes(searchTermLower) ||
        submission.status?.toLowerCase().includes(searchTermLower) ||
        submission.level?.toString().includes(searchTermLower) ||
        (submission.fields?.email?.toLowerCase() || '').includes(searchTermLower) ||
        (submission.fields?.firstName?.toLowerCase() || '').includes(searchTermLower) ||
        (submission.fields?.lastName?.toLowerCase() || '').includes(searchTermLower)
      );
    });
    setFilteredSubmissions(filtered);
  }, [searchTerm, submissions]);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const response = await api.get('/api/admin/reminders');
      setReminders(response.data);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this submission?')) {
      try {
        await api.delete(`/api/admin/submissions/${id}`);
        fetchSubmissions();
        fetchReminders();
      } catch (error) {
        console.error('Error deleting submission:', error);
      }
    }
  };

  const handleRowClick = async (id) => {
    try {
      const response = await api.get(`/api/admin/submissions/${id}`);
      setSelectedSubmission(response.data);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error fetching submission details:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleResendEmails = async (submissionId) => {
    try {
      setResendingEmail(true);
      await api.post(`/api/admin/submissions/${submissionId}/resend-emails`);
      alert('Emails have been resent successfully');
    } catch (error) {
      console.error('Error resending emails:', error);
      alert('Failed to resend emails');
    } finally {
      setResendingEmail(false);
    }
  };

  const getNestedValue = (obj, path, defaultValue = 'N/A') => {
    try {
      // First try to get the value directly from the root level
      if (obj[path]) {
        return obj[path];
      }
      // If not found at root level, try nested path
      const value = path.split('.').reduce((acc, part) => acc[part], obj);
      return value || defaultValue;
    } catch (e) {
      return defaultValue;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'open':
        return 'warning';
      case 'closed':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleViewFile = (upload) => {
    console.log('Viewing file:', upload);
    setViewFile(upload);
  };

  const handleCloseViewer = () => {
    setViewFile(null);
  };

  const renderSubmissionDetails = () => {
    if (!selectedSubmission) return null;

    const { fields } = selectedSubmission;

    const basicInfo = {
      'Submission ID': selectedSubmission.id,
      'Status': selectedSubmission.status || 'N/A',
      'Timestamp': formatDate(selectedSubmission.timestamp),
      'Reference URL': getNestedValue(fields, 'reference_url'),
      'Chosen Language': getNestedValue(fields, 'chosenlanguage'),
    };

    const personalInfo = {
      'Title': getNestedValue(fields, 'title'),
      'First Name': getNestedValue(fields, 'firstName'),
      'Last Name': getNestedValue(fields, 'lastName'),
      'Email': getNestedValue(fields, 'email'),
      'Phone': getNestedValue(fields, 'contactPhone'),
      'Fax': getNestedValue(fields, 'contactFax'),
      'Other Phone': getNestedValue(fields, 'contactOtherPhoneNumber'),
      'Emergency Contact': getNestedValue(fields, 'emergencyContactName'),
      'Emergency Phone': getNestedValue(fields, 'emergencyContactPhone'),
      'Birth Date': fields.birthDate ? formatDate(fields.birthDate) : 'N/A',
      'Birth Place': getNestedValue(fields, 'birthPlace'),
      'Nationality': getNestedValue(fields, 'nationality'),
      'Gender': getNestedValue(fields, 'gender'),
      'Job': getNestedValue(fields, 'job'),
      'Height': fields.height ? `${fields.height} cm` : 'N/A',
      'Weight': fields.weight ? `${fields.weight} kg` : 'N/A',
    };

    const stayInfo = {
      'Arrival Date': fields.arrivalDate ? formatDate(fields.arrivalDate) : 'N/A',
      'Departure Date': fields.departureDate ? formatDate(fields.departureDate) : 'N/A',
      'First Time Visit': fields.firstTime === 'true' ? 'Yes' : 'No',
      'Therapy Type': getNestedValue(fields, 'therapyType'),
      'Therapy Planning': fields.therapyPlanning === 'true' ? 'Yes' : 'No',
      'Medical Care': fields.medicalCare === 'true' ? 'Yes' : 'No',
    };

    const languageInfo = {
      'English': fields.languageEnglish === 'true' ? 'Yes' : 'No',
      'German': fields.languageGerman === 'true' ? 'Yes' : 'No',
      'Spanish': fields.languageSpanish === 'true' ? 'Yes' : 'No',
      'French': fields.languageFrench === 'true' ? 'Yes' : 'No',
      'Other': fields.languageOther === 'true' ? getNestedValue(fields, 'languageOtherDetail') : 'No',
      'Not Fluent': fields.languageIsNotFluent === 'true' ? 'Yes' : 'No',
    };

    const medicalInfo = {
      'Level': getNestedValue(fields, 'level'),
      'Medical History': getNestedValue(fields, 'medical_history'),
      'Allergies': fields.allergiesAny === 'true' ? getNestedValue(fields, 'allergiesAnyDetail') : 'None',
      'Dietary Allergies': fields.allergiesDietary === 'true' ? getNestedValue(fields, 'allergiesDietaryDetail') : 'None',
      'Current Medications': fields.medicationGeneral === 'true' ? 'Yes' : 'No',
      'Other Medications': getNestedValue(fields, 'otherMedication'),
    };

    const goalsInfo = {
      'Spiritual Retreat': fields.goalsSpiritualRetreat === 'true' ? 'Yes' : 'No',
      'Managing Stress': fields.goalsManagingStress === 'true' ? 'Yes' : 'No',
      'Enhancing Longevity': fields.goalsEnhancingLongevity === 'true' ? 'Yes' : 'No',
      'Pursuing Goals': fields.goalsPursuing === 'true' ? 'Yes' : 'No',
      'Exploring Biohacking': fields.goalsExploringBiohacking === 'true' ? 'Yes' : 'No',
      'Deeper Understanding': fields.goalsDeeperUnderstandingBody === 'true' ? 'Yes' : 'No',
      'Coping with Loss': fields.goalsCopingLoss === 'true' ? 'Yes' : 'No',
      'Metabolic Flexibility': fields.goalsMetabolicFlexibility === 'true' ? 'Yes' : 'No',
    };

    const roomPreferences = {
      'Newspaper': fields.roomPreferenceNewspaper === 'true' ? 'Yes' : 'No',
      'Yoga Mat': fields.roomPreferenceYogaMat === 'true' ? 'Yes' : 'No',
      'Flowers': fields.roomPreferenceFlower === 'true' ? 'Yes' : 'No',
      'Other': getNestedValue(fields, 'roomPreferencesOther'),
    };

    const renderInfoSection = (title, data) => (
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              {title}
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(data).map(([key, value]) => (
                <Grid item xs={12} key={key}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {key}
                  </Typography>
                  <Typography variant="body1">
                    {value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );

    return (
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {renderInfoSection('Basic Information', basicInfo)}
          {renderInfoSection('Personal Information', personalInfo)}
          {renderInfoSection('Stay Information', stayInfo)}
          {renderInfoSection('Language Preferences', languageInfo)}
          {renderInfoSection('Medical Information', medicalInfo)}
          {renderInfoSection('Goals', goalsInfo)}
          {renderInfoSection('Room Preferences', roomPreferences)}

          {/* Medications */}
          {fields.medications && fields.medications.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Medications
                  </Typography>
                  <Grid container spacing={2}>
                    {fields.medications.map((med) => (
                      <Grid item xs={12} md={4} key={med.id}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Name
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {med.name}
                          </Typography>
                          <Typography variant="subtitle2" color="text.secondary">
                            Dosage
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {med.dosage}
                          </Typography>
                          {med.frequency && (
                            <>
                              <Typography variant="subtitle2" color="text.secondary">
                                Frequency
                              </Typography>
                              <Typography variant="body1" gutterBottom>
                                {med.frequency}
                              </Typography>
                            </>
                          )}
                          {med.start_date && (
                            <>
                              <Typography variant="subtitle2" color="text.secondary">
                                Start Date
                              </Typography>
                              <Typography variant="body1" gutterBottom>
                                {formatDate(med.start_date)}
                              </Typography>
                            </>
                          )}
                          {med.end_date && (
                            <>
                              <Typography variant="subtitle2" color="text.secondary">
                                End Date
                              </Typography>
                              <Typography variant="body1" gutterBottom>
                                {formatDate(med.end_date)}
                              </Typography>
                            </>
                          )}
                          {med.notes && (
                            <>
                              <Typography variant="subtitle2" color="text.secondary">
                                Notes
                              </Typography>
                              <Typography variant="body1">
                                {med.notes}
                              </Typography>
                            </>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Uploads */}
          {fields.uploads && fields.uploads.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Uploaded Files
                  </Typography>
                  <Grid container spacing={2}>
                    {fields.uploads.map((upload) => (
                      <Grid item xs={12} md={4} key={upload.id}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            File Name
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {upload.original_filename}
                          </Typography>
                          <Typography variant="subtitle2" color="text.secondary">
                            File Size
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {(upload.file_size / 1024).toFixed(2)} KB
                          </Typography>
                          <Typography variant="subtitle2" color="text.secondary">
                            Type
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {upload.content_type}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                            {upload.file_url ? (
                              <>
                                <Button
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => handleViewFile(upload)}
                                >
                                  View
                                </Button>
                                <Button
                                  variant="outlined"
                                  startIcon={<DownloadIcon />}
                                  href={upload.file_url}
                                  target="_blank"
                                >
                                  Download
                                </Button>
                              </>
                            ) : (
                              <Typography color="error">
                                File not found in storage
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Submissions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by email, name, submission ID, or user ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Submissions" />
          <Tab label="Reminders" />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubmissions.map((submission) => (
                  <TableRow
                    key={submission.id}
                    hover
                    onClick={() => handleRowClick(submission.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{submission.id}</TableCell>
                    <TableCell>{submission.email || getNestedValue(submission.fields, 'email')}</TableCell>
                    <TableCell>{formatDate(submission.timestamp)}</TableCell>
                    <TableCell>{submission.level || getNestedValue(submission.fields, 'level')}</TableCell>
                    <TableCell>
                      <Chip
                        label={submission.status || 'N/A'}
                        color={getStatusColor(submission.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {submission.status?.toLowerCase() === 'completed' && (
                        <Tooltip title="Resend Emails">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResendEmails(submission.id);
                            }}
                            color="primary"
                            size="small"
                            disabled={resendingEmail}
                          >
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Submission">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(submission.id);
                          }}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      )}

      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Reminder</TableCell>
                <TableCell>Submission Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reminders.map((reminder) => (
                <TableRow
                  key={reminder.id}
                  hover
                  onClick={() => handleRowClick(reminder.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{reminder.id}</TableCell>
                  <TableCell>{reminder.name || 'N/A'}</TableCell>
                  <TableCell>{reminder.email || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={reminder.status}
                      color={reminder.status === 'open' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {reminder.last_reminder ? formatDate(reminder.last_reminder) : 'Never'}
                  </TableCell>
                  <TableCell>{formatDate(reminder.timestamp)}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(reminder.id);
                      }}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Submission Details
        </DialogTitle>
        <DialogContent>
          {renderSubmissionDetails()}
        </DialogContent>
        <DialogActions>
          {selectedSubmission?.status?.toLowerCase() === 'completed' && (
            <Button
              onClick={(e) => handleResendEmails(selectedSubmission.id)}
              startIcon={<EmailIcon />}
              disabled={resendingEmail}
            >
              Resend Emails
            </Button>
          )}
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        open={!!viewFile}
        onClose={handleCloseViewer}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {viewFile?.original_filename}
        </DialogTitle>
        <DialogContent>
          {viewFile && (
            <Box sx={{ width: '100%', height: '80vh' }}>
              {viewFile.content_type.startsWith('image/') ? (
                <img
                  src={viewFile.file_url}
                  alt={viewFile.original_filename}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => {
                    console.error('Error loading image:', e);
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDEzIDEwIDUgMjEgNSAzIj48L3BvbHlsaW5lPjwvc3ZnPg==';
                  }}
                />
              ) : viewFile.content_type === 'application/pdf' ? (
                <iframe
                  src={viewFile.file_url}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title={viewFile.original_filename}
                  onError={(e) => {
                    console.error('Error loading PDF:', e);
                  }}
                />
              ) : (
                <Typography>Unsupported file type</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewer}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Submissions; 